import React, { useRef, useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Stack,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  useMediaQuery
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Schedule as ScheduleIcon,
  Block as BlockIcon,
  VideoLibrary as VideoIcon,
  Lightbulb as LightbulbIcon,
  AccountCircle as AccountCircleIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import TutorialTip from './TutorialTip';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  zIndex: '10000 !important',
  '& .MuiDialog-container': {
    zIndex: 10000
  },
  '& .MuiDialog-paper': {
    borderRadius: theme.shape.borderRadius * 3,
    maxWidth: 900,
    minWidth: 750,
    overflow: 'hidden',
    boxShadow: theme.shadows[24],
    background: 'linear-gradient(to bottom, #ffffff 0%, #f8f9fa 100%)',
    zIndex: 10000,
    position: 'relative',
    [theme.breakpoints.down('sm')]: {
      minWidth: 'auto',
      margin: theme.spacing(2)
    }
  },
  '& .MuiBackdrop-root': {
    zIndex: 9999
  },
  '& .MuiDialogContent-root': {
    zIndex: 10001,
    position: 'relative'
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: '#fff',
  padding: theme.spacing(3, 3, 2.5, 3),
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 1,
    background: 'rgba(255, 255, 255, 0.1)'
  }
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(3),
  padding: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    padding: theme.spacing(2)
  }
}));

const MainContent = styled(Box)({
  flex: 1,
  minWidth: 0
});

const FeatureBadge = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: '#fff',
  fontWeight: 600,
  fontSize: '0.75rem',
  height: 24,
  '& .MuiChip-label': {
    padding: theme.spacing(0, 1.5)
  }
}));

const VideoPreview = styled(Box)(({ theme }) => ({
  width: '100%',
  height: 280,
  backgroundColor: '#000',
  borderRadius: theme.shape.borderRadius * 2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  overflow: 'hidden',
  marginBottom: theme.spacing(3),
  cursor: 'pointer',
  boxShadow: theme.shadows[8],
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[12],
    '& .play-button': {
      transform: 'scale(1.15)',
      backgroundColor: 'rgba(255, 255, 255, 1)'
    },
    '&::after': {
      backgroundColor: 'rgba(0, 0, 0, 0.2)'
    }
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.4) 100%)',
    zIndex: 1
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.35)',
    zIndex: 1,
    transition: 'background-color 0.3s ease-in-out'
  },
  '& video': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    position: 'absolute',
    top: 0,
    left: 0
  },
  '& .play-button': {
    position: 'relative',
    zIndex: 2,
    width: 80,
    height: 80,
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
    '& svg': {
      fontSize: 40,
      color: (theme) => theme.palette.primary.main,
      marginLeft: 4
    }
  }
}));

const DescriptionBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  borderRadius: theme.shape.borderRadius * 1.5,
  padding: theme.spacing(2.5),
  marginBottom: theme.spacing(3),
  borderLeft: `4px solid ${theme.palette.primary.main}`,
  display: 'flex',
  gap: theme.spacing(1.5),
  alignItems: 'flex-start'
}));

const ActionButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  borderRadius: theme.shape.borderRadius * 1.5,
  padding: theme.spacing(1.25, 3),
  fontWeight: 600,
  fontSize: '0.95rem',
  boxShadow: 'none',
  transition: 'all 0.2s ease-in-out',
  '&.primary': {
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: '#fff',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows[8],
      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`
    }
  },
  '&.secondary': {
    borderWidth: 2,
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows[4],
      backgroundColor: theme.palette.action.hover
    }
  },
  '&.text': {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      transform: 'translateY(-1px)'
    }
  }
}));

const SidebarContent = styled(Box)(({ theme }) => ({
  width: 280,
  minWidth: 280,
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    minWidth: 'auto'
  }
}));

const TutorialList = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  borderRadius: theme.shape.borderRadius * 1.5,
  padding: theme.spacing(1),
  maxHeight: 400,
  overflowY: 'auto',
  '& .MuiListItemButton-root': {
    borderRadius: theme.shape.borderRadius,
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    }
  }
}));

const InfoDialog = styled(Dialog)(({ theme }) => ({
  zIndex: '10002 !important',
  '& .MuiDialog-container': {
    zIndex: 10002
  },
  '& .MuiDialog-paper': {
    borderRadius: theme.shape.borderRadius * 2,
    maxWidth: 500,
    padding: theme.spacing(1),
    zIndex: 10002,
    position: 'relative'
  },
  '& .MuiBackdrop-root': {
    zIndex: 10001
  }
}));

const ConfirmationDialog = styled(Dialog)(({ theme }) => ({
  zIndex: '10003 !important',
  '& .MuiDialog-container': {
    zIndex: 10003
  },
  '& .MuiDialog-paper': {
    borderRadius: theme.shape.borderRadius * 2,
    maxWidth: 500,
    padding: theme.spacing(1),
    zIndex: 10003,
    position: 'relative'
  },
  '& .MuiBackdrop-root': {
    zIndex: 10002
  }
}));

/**
 * Tutorial Prompt Component
 * Full prompt shown for the first feature tutorial in a session
 */
const TutorialPrompt = ({
  open,
  onClose,
  featureName,
  description,
  videoSrc,
  onPlay,
  onRemindLater,
  onDontShow,
  otherTutorials = []
}) => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const videoRef = useRef(null);
  const [showInfoDialog, setShowInfoDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const pendingCallbackRef = useRef(null);

  // Load video poster (3rd frame) when dialog opens
  // Using preload="metadata" and seeking to a small offset (0.3-1s) is efficient
  // Browsers typically cache a few frames near the start without downloading the full video
  useEffect(() => {
    if (!open || !videoSrc) return;

    const video = videoRef.current;
    if (!video) return;

    let isMounted = true;

    // Handler to seek to 3rd frame after metadata loads
    const handleSeekToThirdFrame = () => {
      if (!isMounted || !video) return;

      try {
        // Use a small time offset (0.3-1 second) to get the 3rd frame
        // This is still efficient and doesn't trigger full video download
        // Browsers typically cache initial frames (first 1-2 seconds) with metadata
        // Seeking to a small offset usually only downloads a few KB of data
        // At 30fps, 0.3-1 second = approximately 3rd-30th frame
        const thumbnailTime = Math.min(1.0, video.duration > 0 ? Math.max(0.3, video.duration * 0.02) : 0.5);
        
        // Seek to the thumbnail time
        // The browser will try to load only what's needed for that frame
        // Since it's a small offset (< 1s), it typically doesn't download much
        // Modern browsers are optimized to cache initial video segments efficiently
        video.currentTime = thumbnailTime;
      } catch (error) {
        // If seeking fails, fallback to first frame
        console.log('Could not seek to 3rd frame, using first frame:', error);
        if (video && isMounted) {
          video.currentTime = 0;
        }
      }
    };

    // Load thumbnail at a small time offset (3rd frame, ~0.3-1 seconds)
    const loadThumbnail = () => {
      try {
        // Load metadata first (lightweight - only downloads headers and first frame)
        if (video.readyState === 0) {
          video.load();
        }

        // If metadata is already loaded, seek immediately
        if (video.readyState >= 1 && video.duration) {
          const thumbnailTime = Math.min(1.0, Math.max(0.3, video.duration * 0.02));
          video.currentTime = thumbnailTime;
        } else {
          // Wait for metadata to load, then seek to 3rd frame
          // Using { once: true } ensures the listener is removed automatically after firing
          video.addEventListener('loadedmetadata', handleSeekToThirdFrame, { once: true });
        }
      } catch (error) {
        console.log('Error loading video thumbnail:', error);
        // Fallback to first frame
        if (video && isMounted) {
          video.currentTime = 0;
        }
      }
    };

    // Wait for dialog to render before accessing video element
    const timer = setTimeout(() => {
      if (video && open && isMounted) {
        loadThumbnail();
      }
    }, 100);

    return () => {
      isMounted = false;
      clearTimeout(timer);
      // Cleanup event listener if component unmounts before event fires
      // Using { once: true } means it auto-removes after firing, but we clean up manually too
      if (video) {
        video.removeEventListener('loadedmetadata', handleSeekToThirdFrame);
      }
    };
  }, [open, videoSrc]);

  const handlePlay = () => {
    if (onPlay) {
      onPlay();
    }
  };

  const handleRemindLater = () => {
    // Store the callback to call later, don't call it now
    // This prevents it from closing the dialog before info dialog shows
    pendingCallbackRef.current = onRemindLater;
    // Show info dialog - don't call callback yet
    setShowInfoDialog(true);
  };

  const handleDontShow = () => {
    // Show confirmation dialog first - this is a major irreversible action
    setShowConfirmDialog(true);
  };

  const handleConfirmDontShow = async () => {
    // User confirmed - store callback to execute after info dialog closes
    // This prevents the component from unmounting before info dialog shows
    if (onDontShow) {
      pendingCallbackRef.current = onDontShow;
    }
    // Close confirmation dialog
    setShowConfirmDialog(false);
    // Show info dialog to inform user how to access tutorials later
    setShowInfoDialog(true);
  };

  const handleCancelDontShow = () => {
    // User cancelled - just close confirmation dialog
    setShowConfirmDialog(false);
  };

  const handleCloseInfoDialog = async () => {
    // Close info dialog first
    setShowInfoDialog(false);
    // Call the pending callback if there is one (this might call onClose)
    if (pendingCallbackRef.current) {
      const callback = pendingCallbackRef.current;
      pendingCallbackRef.current = null;
      // Await the callback in case it's async (like onDontShow which updates database)
      await callback();
    }
    // Then close main dialog
    onClose();
  };

  return (
    <>
    <StyledDialog
      open={open}
      onClose={(showInfoDialog || showConfirmDialog) ? undefined : onClose}
      aria-labelledby="tutorial-prompt-title"
      aria-describedby="tutorial-prompt-description"
      slotProps={{
        backdrop: {
          sx: { 
            zIndex: 9999,
            ...((showInfoDialog || showConfirmDialog) && {
              backgroundColor: 'transparent'
            })
          }
        }
      }}
      sx={{
        zIndex: 10000,
        '& .MuiDialog-container': {
          zIndex: 10000
        },
        ...((showInfoDialog || showConfirmDialog) && {
          '& .MuiDialog-paper': {
            pointerEvents: 'none'
          }
        })
      }}
    >
      <StyledDialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <VideoIcon sx={{ fontSize: 28, color: '#fff' }} />
            </Box>
            <Box>
              <Box display="flex" alignItems="center" gap={1.5} mb={0.5}>
                <FeatureBadge
                  icon={<LightbulbIcon sx={{ fontSize: 14, color: '#fff !important' }} />}
                  label="New Tutorial"
                  size="small"
                />
              </Box>
              <Typography
                variant="h5"
                id="tutorial-prompt-title"
                sx={{
                  fontWeight: 700,
                  color: '#fff',
                  lineHeight: 1.2
                }}
              >
                {featureName}
              </Typography>
            </Box>
          </Box>
        </Box>
      </StyledDialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <ContentContainer>
          <MainContent>
            {description && (
              <DescriptionBox>
                <LightbulbIcon
                  sx={{
                    color: 'primary.main',
                    fontSize: 24,
                    mt: 0.5,
                    flexShrink: 0
                  }}
                />
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    lineHeight: 1.7,
                    fontSize: '1rem'
                  }}
                >
                  {description}
                </Typography>
              </DescriptionBox>
            )}

            {videoSrc && (
              <VideoPreview
                onClick={handlePlay}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handlePlay();
                  }
                }}
                aria-label="Click to play tutorial video"
              >
                <video
                  ref={videoRef}
                  src={videoSrc}
                  muted
                  playsInline
                  preload="metadata"
                  // Seeking to a small offset (0.3-1s) after metadata loads
                  // This shows the 3rd frame without downloading the full video
                  // Browsers typically cache initial frames (first 1-2 seconds) efficiently
                  style={{ 
                    pointerEvents: 'none',
                    objectFit: 'cover'
                  }}
                  aria-hidden="true"
                />
                <Box className="play-button">
                  <PlayIcon />
                </Box>
              </VideoPreview>
            )}

            <Stack spacing={2} sx={{ mt: 2 }}>
              <ActionButton
                variant="contained"
                className="primary"
                startIcon={<PlayIcon />}
                onClick={handlePlay}
                fullWidth
                size="large"
              >
                Watch Tutorial Now
              </ActionButton>
              <Box display="flex" gap={1.5} flexWrap="wrap">
                <ActionButton
                  variant="outlined"
                  className="secondary"
                  startIcon={<ScheduleIcon />}
                  onClick={handleRemindLater}
                  sx={{ flex: 1, minWidth: isMobile ? 100 : 140, py: isMobile ? 0.75 : undefined, px: isMobile ? 1.5 : undefined, fontSize: isMobile ? '0.8rem' : '0.95rem' }}
                >
                  Remind Later
                </ActionButton>
                <ActionButton
                  variant="outlined"
                  className="secondary"
                  startIcon={<BlockIcon />}
                  onClick={handleDontShow}
                  sx={{ flex: 1, minWidth: isMobile ? 100 : 140, py: isMobile ? 0.75 : undefined, px: isMobile ? 1.5 : undefined, fontSize: isMobile ? '0.8rem' : '0.95rem' }}
                >
                  Don't Show
                </ActionButton>
              </Box>
            </Stack>

            <TutorialTip onClose={onClose} />
          </MainContent>
          
          {/* Sidebar with other tutorials */}
          {otherTutorials.length > 0 && (
            <SidebarContent>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Other Tutorials
              </Typography>
              <TutorialList>
                <List dense>
                  {otherTutorials.map((tutorial, index) => (
                    <React.Fragment key={tutorial.featureId || index}>
                      <ListItem disablePadding>
                        <ListItemButton 
                          onClick={() => tutorial.onClick?.()}
                          sx={{ py: 1.5 }}
                        >
                          <ListItemText
                            primary={tutorial.title}
                            primaryTypographyProps={{ 
                              variant: 'body2',
                              fontWeight: 500 
                            }}
                            secondary={tutorial.description}
                            secondaryTypographyProps={{ 
                              variant: 'caption',
                              sx: { mt: 0.5 }
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                      {index < otherTutorials.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </TutorialList>
            </SidebarContent>
          )}
        </ContentContainer>
      </DialogContent>
    </StyledDialog>

    {/* Confirmation Dialog - Shows when user clicks "Don't show" */}
    <ConfirmationDialog
      open={showConfirmDialog}
      onClose={handleCancelDontShow}
      aria-labelledby="confirm-dont-show-title"
      aria-describedby="confirm-dont-show-description"
      slotProps={{
        backdrop: {
          sx: { 
            zIndex: 10002,
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          }
        }
      }}
      sx={{
        zIndex: 10003,
        '& .MuiDialog-container': {
          zIndex: 10003
        },
        '& .MuiDialog-paper': {
          zIndex: 10003,
          position: 'relative'
        }
      }}
    >
      <DialogTitle id="confirm-dont-show-title">
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              backgroundColor: 'grey.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <InfoIcon sx={{ color: 'text.secondary', fontSize: 24 }} />
          </Box>
          <Typography variant="h6" fontWeight={600}>
            Confirm Disable Tutorials
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Typography
            id="confirm-dont-show-description"
            variant="body1"
            color="text.secondary"
            sx={{ mb: 2, lineHeight: 1.7 }}
          >
            Are you sure you want to select "Don't Show"?
          </Typography>
          <Box
            sx={{
              backgroundColor: 'grey.50',
              padding: 2,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.200',
              mb: 2
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.6 }}
            >
              If you continue, we will never suggest any tutorial videos again for any feature. This action applies globally to all tutorials.
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1.5 }}>
        <ActionButton
          onClick={handleCancelDontShow}
          variant="outlined"
          className="secondary"
          sx={{ flex: 1 }}
        >
          Cancel
        </ActionButton>
        <ActionButton
          onClick={handleConfirmDontShow}
          variant="contained"
          className="primary"
          sx={{ flex: 1 }}
        >
          Yes, Disable All
        </ActionButton>
      </DialogActions>
    </ConfirmationDialog>

    {/* Simple Info Dialog - Shows when user clicks "Remind me later" or "Don't show" */}
    <InfoDialog
      open={showInfoDialog}
      onClose={handleCloseInfoDialog}
      aria-labelledby="tutorial-info-title"
      aria-describedby="tutorial-info-description"
      slotProps={{
        backdrop: {
          sx: { 
            zIndex: 10001,
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          }
        }
      }}
      sx={{
        zIndex: 10002,
        '& .MuiDialog-container': {
          zIndex: 10002
        },
        '& .MuiDialog-paper': {
          zIndex: 10002,
          position: 'relative'
        }
      }}
    >
      <DialogTitle id="tutorial-info-title">
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              backgroundColor: 'primary.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <InfoIcon sx={{ color: 'primary.main', fontSize: 24 }} />
          </Box>
          <Typography variant="h6" fontWeight={600}>
            Access Tutorials Anytime
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Typography
            id="tutorial-info-description"
            variant="body1"
            color="text.secondary"
            sx={{ mb: 3, lineHeight: 1.7 }}
          >
            You can always access tutorials by selecting your profile and then selecting tutorials.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              backgroundColor: 'grey.50',
              padding: 2,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.200'
            }}
          >
            <AccountCircleIcon sx={{ color: 'primary.main', fontSize: 28 }} />
            <Box>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                Profile Menu
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Click your profile icon → Select "Tutorials"
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <ActionButton
          onClick={handleCloseInfoDialog}
          variant="contained"
          className="primary"
          fullWidth
        >
          Got it
        </ActionButton>
      </DialogActions>
    </InfoDialog>
    </>
  );
};

export default TutorialPrompt;

